<template>
  <div>
    <v-snackbar v-model="snackbar" :top="true" :color="color" :timeout="6000">
      <span v-if="snackbar_msg != null">{{ snackbar_msg }}</span>
      <v-btn dark text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
    <v-toolbar flat dense class="custom-toolbar">
      <v-toolbar-title>Student Award Details</v-toolbar-title>
      <v-divider class="mx-4" inset vertical></v-divider>
    </v-toolbar>
    <v-data-table :headers="dynamicHeaders" :items="awardDetails.awards" class="elevation-1 ma-5" sort-by="srno"
      :search="search" hide-default-header>
      <!-- Header slot to apply background color -->
      <template v-slot:header="{ props }">
        <thead>
          <tr>
            <th v-for="header in props.headers" :key="header.value" class="custom-header">
              {{ header.text }}
            </th>
          </tr>
        </thead>
      </template>
      <template v-slot:top>
        <v-toolbar flat dense color="white">
          <v-spacer></v-spacer>
          <v-text-field v-model="search" append-icon="search" label="Search" style="padding: 10px" single-line
            hide-details></v-text-field>
          <span style="width: 20px"></span>
          <v-dialog v-model="dialog" max-width="800px">
            <template v-slot:activator="{ on }">
              <v-btn class="button" v-on="on" small>Add Award Details</v-btn>
            </template>
            <v-card>
              <v-card-title class="custom-toolbar">
                <span class="headline">{{ formTitle }}</span>
              </v-card-title>
              <v-card-text>
                <v-container>
                  <v-row dense>
                    <v-col v-for="field in fieldConfigs" :key="field.field_name" v-if="field.isactive" cols="12" sm="6">
                      <div class="pl-1 pb-2" style="font-size: 14px">
                        <b>{{ field.label }}</b>
                        <v-icon v-if="field.required && isFieldInvalid(field)" class="pl-1 pb-2"
                          color="error">*</v-icon>
                      </div>
                      <!-- Text Field -->
                      <v-text-field v-if="field.input_type === 'text'" v-model.trim="formData[field.field_name]"
                        :readonly="!field.iseditable" :filled="!field.iseditable" outlined rounded dense
                        :error="!!getFieldError(field)"
                        :error-messages="getFieldError(field) ? [getFieldError(field)] : []"
                        @keypress="field.field_name === 'title' ? blockNonLetters : null"
                        @blur="validateField(field)" />
                      <!-- Textarea -->
                      <v-textarea v-if="field.input_type === 'textarea'" v-model.trim="formData[field.field_name]"
                        :readonly="!field.iseditable" :filled="!field.iseditable" outlined rounded dense
                        :rules="[v => (!v || v.length <= 250) || 'Max 250 characters']" :error="!!getFieldError(field)"
                        :error-messages="getFieldError(field) ? [getFieldError(field)] : []"
                        @blur="validateField(field)" />
                      <!-- Select Field -->
                      <v-autocomplete v-if="field.input_type === 'select'" v-model="formData[field.field_name]"
                        :items="field.options" :readonly="!field.iseditable" :filled="!field.iseditable"
                        :multiple="field.multiple" :clearable="true" dense single-line rounded outlined
                        :error="!!getFieldError(field)"
                        :error-messages="getFieldError(field) ? [getFieldError(field)] : []"
                        @blur="validateField(field)" />
                      <!-- File Input -->

                      <div v-if="field.input_type === 'file'">
                        <v-file-input v-model="formData[field.field_name]" :readonly="!field.iseditable"
                          :filled="!field.iseditable" :disabled="!field.iseditable" prepend-icon="mdi-file-document"
                          show-size outlined rounded dense :accept="allowed_extensions.join(',')"
                          @change="handleFileChange(field, $event)" />
                        <!-- Custom error message display -->
                        <div v-if="getFieldError(field)" class="v-messages v-messages__message error--text"
                          style="font-size: 12px; margin-top: 4px;">
                          {{ getFieldError(field) }}
                        </div>
                        <!-- Preview -->
                        <div v-if="formData[field.field_name + '_preview']" class="mt-2">
                          <span><b>Selected File</b></span>
                          <div style="border: 1px solid black; width: 180px; height: 180px;">
                            <img :src="formData[field.field_name + '_preview']" width="180" height="180" />
                          </div>
                        </div>
                        <div v-if="formData[field.field_name + '_old'] && editedIndex !== -1" class="mt-2">
                          <span><b>Previous File</b></span>
                          <a :href="aws_path + formData[field.field_name + '_old']" target="_blank">View</a>
                        </div>
                        <!-- <span style="font-weight: bold; font-size: medium; color: #f93a13;">Note: File Should Be In PDF
                          ,JPG ,PNG Formats.</span> -->
                      </div>

                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn class="button" small @click="close">Cancel</v-btn>
                <v-btn class="button-77" text @click="save">Save</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>
      <template v-slot:item="{ item }">
        <tr>
          <td>{{ item.srno }}</td>
          <td v-for="field in item.fields" :key="field.field_name" v-if="field.isactive">
            <span v-if="field.input_type !== 'file'">{{ field.value || '--' }}</span>
            <span v-else-if="field.value !== 'NA'">
              <v-icon color="primary" @click="openDoc(field.value)" class="cursor-pointer">mdi-eye</v-icon>
            </span>
            <span v-else>--</span>
          </td>
          <td>
            <v-avatar size="20" class="edit-avatar">
              <v-icon color="success" class="edit-v-icon mr-2" @click="editItem(item)">edit</v-icon>
            </v-avatar>
            <v-avatar size="20" class="edit-avatar">
              <v-icon color="error" class="delete-v-icon mr-2" @click="deleteItem(item)">delete</v-icon>
            </v-avatar>
          </td>
        </tr>
      </template>
    </v-data-table>
    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <div class="text-center">
      <v-dialog v-model="dialog_upload" hide-overlay persistent width="300">
        <v-card color="primary" dark>
          <v-card-text>
            Please wait..
            <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
  import axios from "axios";
  import "@/assets/css/color.css";

  export default {
    props: {
      learner: {
        type: [String, Number],
        required: true,
      },
    },
    data() {
      return {
        overlay: false,
        dialog_upload: false,
        snackbar: false,
        snackbar_msg: "",
        color: "",
        search: "",
        dialog: false,
        editedIndex: -1,
        awardDetails: {
          learner_id: "",
          registration_number: "",
          awards: [],
        },
        fieldConfigs: [],
        formData: {},
        touchedFields: {},
        fieldErrors: {},
        dynamicHeaders: [
          { text: "Sr. No.", align: "left", sortable: false, value: "srno" },
        ],
        allowed_extensions: [],
        sizelimit: null,
        aws_path: null,
        award_type_list: [],
        ay_list: [],
        isSubmitting: false,
      };
    },
    computed: {
      formTitle() {
        return this.editedIndex === -1 ? "New Award Details" : "Edit Award Details";
      },
    },
    watch: {
      learner(newVal) {
        if (newVal) {
          this.getData();
        }
      },
      dialog(newVal) {
        if (newVal) {
          this.initializeForm();
        }
      },
    },
    mounted() {
      this.learner = localStorage.getItem("learner_for_profile");
      this.aws_path = localStorage.getItem("awspath");
      if (this.learner) {
        this.getData();
      }
    },
    methods: {
      showSnackbar(clr, msg) {
        this.snackbar = true;
        this.color = clr;
        this.snackbar_msg = msg;
      },
      blockNonLetters(e) {
        const char = String.fromCharCode(e.keyCode || e.which);
        if (!/^[a-zA-Z\s]$/.test(char)) e.preventDefault();
      },
      initializeForm() {
        this.formData = {};
        this.touchedFields = {};
        this.fieldErrors = {};
        this.fieldConfigs.forEach(fc => {
          this.$set(this.formData, fc.field_name, fc.input_type === "file" ? null : "");
          this.$set(this.touchedFields, fc.field_name, false);
          this.$set(this.fieldErrors, fc.field_name, "");
          if (fc.input_type === "file") {
            this.$set(this.formData, fc.field_name + "_preview", "");
            this.$set(this.formData, fc.field_name + "_old", this.editedIndex !== -1 ? (this.awardDetails.awards[this.editedIndex].fields.find(f => f.field_name === fc.field_name).value || "") : "");
          }
        });
        if (this.editedIndex !== -1) {
          const item = this.awardDetails.awards[this.editedIndex];
          item.fields.forEach(field => {
            this.$set(this.formData, field.field_name, field.input_type === "file" ? null : field.value);
          });
          this.$set(this.formData, 'id', item.id);
        }
      },
      isFieldInvalid(field) {
        const fieldName = field.field_name;
        if (!field.required) return false;
        if (field.input_type === "file") {
          return !this.formData[fieldName] && !this.formData[fieldName + "_old"];
        }
        const value = this.formData[fieldName];
        return !value || (typeof value === "string" && value.trim() === "");
      },
      getFieldError(field) {
        const fieldName = field.field_name;
        if (!this.touchedFields[fieldName] && !this.isSubmitting) return "";
        if (field.input_type === "file") {
          if (field.required && !this.formData[fieldName] && !this.formData[fieldName + "_old"]) {
            return `${field.label} is required`;
          }
          return this.fieldErrors[fieldName] || "";
        }
        if (field.required && this.isFieldInvalid(field)) {
          return `${field.label} is required`;
        }
        if (field.input_type === "textarea" && this.formData[fieldName] && this.formData[fieldName].length > 250) {
          return `${field.label} must be 250 characters or less`;
        }
        return this.fieldErrors[fieldName] || "";
      },
      validateField(field) {
        const fieldName = field.field_name;
        this.$set(this.touchedFields, fieldName, true);
        if (field.input_type === "file") {
          this.handleFileChange(field, this.formData[fieldName]);
        }
        this.$set(this.fieldErrors, fieldName, this.getFieldError(field));
      },
      handleFileChange(field, file) {
        const name = field.field_name;
        this.$set(this.touchedFields, name, true);
        this.$set(this.fieldErrors, name, "");
        this.$set(this.formData, name + "_preview", "");

        if (!file) {
          if (field.required && !this.formData[name + "_old"]) {
            this.$set(this.fieldErrors, name, `${field.label} is required`);
          }
          this.$set(this.formData, name, null);
          return;
        }

        const ext = file.name.toLowerCase().split('.').pop();
        const valid = ['pdf', 'jpg', 'jpeg', 'png'];
        if (!valid.includes(ext)) {
          this.$set(this.fieldErrors, name, "Only PDF, JPG, JPEG, PNG files are allowed!");
          this.$set(this.formData, name, null);
          this.showSnackbar("error", "Invalid file format!");
          return;
        }

        const maxSize = this.sizelimit || 1048576;
        if (file.size > maxSize) {
          this.$set(this.fieldErrors, name, `File size must be ≤ ${maxSize / 1024} KB`);
          this.$set(this.formData, name, null);
          this.showSnackbar("error", "File too large!");
          return;
        }

        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = e => this.$set(this.formData, name + "_preview", e.target.result);
          reader.readAsDataURL(file);
        }

        this.$set(this.formData, name, file);
      },
      getData() {
        const formData = { learner: this.learner };
        axios
          .post("/EmployeeProfile/getStudentAwardsNew", formData)
          .then((res) => {
            if (res.data.flag === "YES") {
              this.awardDetails = res.data.awardDetails;
              this.fieldConfigs = res.data.fieldConfigs;
              this.allowed_extensions = res.data.allowed_extensions || ["pdf", "JPG", "PNG"];
              this.sizelimit = res.data.sizelimit || 1048576;
              this.aws_path = res.data.aws_path;
              this.award_type_list = res.data.award_type_list || [];
              this.ay_list = res.data.ay_list || [];
              this.dynamicHeaders = [
                { text: "Sr. No.", align: "left", sortable: false, value: "srno" },
                ...this.fieldConfigs
                  .filter(fc => fc.isactive)
                  .map(fc => ({
                    text: fc.label,
                    align: "left",
                    sortable: true,
                    value: fc.field_name,
                  })),
                { text: "Actions", value: "action", sortable: false },
              ];
            } else {
              this.showSnackbar("error", res.data.snackbar);
            }
          })
          .catch((error) => {
            this.showSnackbar("error", "Failed to fetch award data.");
            console.error(error);
          });
      },
      editItem(item) {
        this.editedIndex = this.awardDetails.awards.indexOf(item);
        this.dialog = true;
      },
      close() {
        this.dialog = false;
        this.editedIndex = -1;
        this.isSubmitting = false;
      },
      save() {
        let hasErrors = false;
        this.fieldConfigs.forEach(field => {
          this.validateField(field);
          if (this.getFieldError(field)) {
            hasErrors = true;
          }
        });
        if (hasErrors) {
          this.showSnackbar("error", "Please fix the errors below before saving.");
          this.isSubmitting = false;
          return;
        }

        this.isSubmitting = true;
        this.dialog_upload = true;
        let formData = new FormData();
        formData.append("learner", this.learner);
        if (this.editedIndex > -1) {
          formData.append("id", this.formData.id);
        }
        this.fieldConfigs.forEach(fc => {
          if (fc.input_type !== "file") {
            formData.append(fc.field_name, this.formData[fc.field_name] ? String(this.formData[fc.field_name]).trim() : "");
          } else if (this.formData[fc.field_name]) {
            formData.append(fc.field_name, this.formData[fc.field_name]);
          } else if (this.formData[fc.field_name + "_old"]) {
            formData.append(fc.field_name + "_old", this.formData[fc.field_name + "_old"]);
          }
        });

        const endpoint = this.editedIndex > -1 ? "/EmployeeProfile/editStudentAwardsNew" : "/EmployeeProfile/saveStudentAwardsNew";
        axios
          .post(endpoint, formData, { headers: { "Content-Type": "multipart/form-data" } })
          .then((res) => {
            if (res.data.flag === "YES") {
              this.showSnackbar("success", res.data.snackbar);
              this.getData();
            } else {
              this.showSnackbar("error", res.data.snackbar);
            }
          })
          .catch((error) => {
            this.showSnackbar("error", "Failed to save award data.");
            console.error(error);
          })
          .finally(() => {
            this.dialog_upload = false;
            this.close();
            this.isSubmitting = false;
          });
      },
      deleteItem(item) {
        if (confirm("Are you sure you want to delete?")) {
          const formData = {
            learner: this.learner,
            inst_award_id: item.id,
          };
          axios
            .post("/EmployeeProfile/deleteStudentAwardsNew", formData)
            .then((res) => {
              if (res.data.flag === "YES") {
                this.showSnackbar("success", res.data.snackbar);
                this.getData();
              } else {
                this.showSnackbar("error", res.data.snackbar);
              }
            })
            .catch((error) => {
              this.showSnackbar("error", "Failed to delete award.");
              console.error(error);
            });
        }
      },
      openDoc(document) {
        window.open(this.aws_path + document, "_blank");
      },
    },
  };
</script>

<style scoped>
  .custom-toolbar {
    background: linear-gradient(to right, #a8edea, #e4d0ff);
  }

  .custom-header {
    background: linear-gradient(to right, #ffe0b2, #ffccbc);
    color: black !important;
    font-weight: bold;
  }

  .button-77 {
    color: #fff;
    cursor: pointer;
    font-weight: 800;
    vertical-align: middle;
    z-index: 0;
  }

  .button-77:before,
  .button-77:after {
    border-radius: 80px;
  }

  .button-77:before {
    background-color: rgba(249, 58, 19, 0.32);
    content: "";
  }

  .button-77:after {
    background-image: linear-gradient(92.83deg, #ff7426 0, #f93a13 100%);
    bottom: 4px;
    content: "";
    display: block;
    left: 4px;
    overflow: hidden;
    position: absolute;
    right: 4px;
    top: 4px;
    transition: all 100ms ease-out;
    z-index: -1;
  }

  .button-77:hover:not(:disabled):after {
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
    transition-timing-function: ease-in;
  }

  .button-77:active:not(:disabled) {
    color: #ccc;
  }

  .button-77:active:not(:disabled):after {
    background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(92.83deg, #ff7426 0, #f93a13 100%);
    bottom: 4px;
    left: 4px;
    right: 4px;
    top: 4px;
  }

  .button-77:disabled {
    cursor: default;
    opacity: 0.24;
  }
</style>